import React, { useEffect, useRef, useState } from "react";
import { ago, dateTime, isStale } from "../format";
import ReportDownloadModal from "./ReportDownloadModal";
import { generateInventoryPdf } from "../generateInventoryPdf";

const STAGE_TEXT = {
  queued: "Fetch job queued",
  callingAmazon: "Fetching from Amazon",
  waitingForAmazonReports: "Waiting for Amazon to prepare reports",
  done: "Fetch finished",
  failed: "Fetch failed",
};

const TAB_SECTION_MAP = {
  actions: "actionCenter",
  inventory: "inventoryHealth",
  inbound: "inbound",
  recovery: "moneyRecovery",
  settings: null,
  health: "systemHealth",
};

const TAB_LABELS = {
  actions: "Action Center",
  inventory: "Inventory Health",
  inbound: "Inbound Shipments",
  recovery: "Money Recovery",
  settings: "Alert Settings",
  health: "System Health",
};

/**
 * Right side of the page header: when the data was collected, plus the refresh and export actions.
 *  Reload            -> rebuilds the screens from stored data (seconds)
 *  Fetch from Amazon -> runs the scheduled job for this store now (minutes)
 *  PDF Report        -> exports comprehensive or custom PDF report
 */
export default function SnapshotBar({
  snapshot,
  asOf,
  loading,
  onReload,
  job,
  jobRunning,
  jobError,
  onFetch,
  data,
  currentTab = "actions",
  currency = "USD",
}) {
  const [force, setForce] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [quickDownloading, setQuickDownloading] = useState(false);
  const dropdownRef = useRef(null);

  const times = [
    snapshot && snapshot.lastApiCallAt,
    snapshot && snapshot.reportsFetchedAt,
    snapshot && snapshot.oldestSnapshotReportAt,
  ].filter(Boolean);
  const dataTime = times.length
    ? times.reduce((latest, t) => (new Date(t) > new Date(latest) ? t : latest))
    : null;
  const stale = isStale(dataTime);
  const reports = job && job.reports;

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleQuickDownload = async (type) => {
    if (!data) return;
    setQuickDownloading(true);
    setShowExportDropdown(false);
    try {
      if (type === "full") {
        await generateInventoryPdf(data, {
          sellerName: (data.store && data.store.name) || "Seller",
          periodText: "This week",
          currency,
          sections: {
            actToday: true,
            dueThisWeek: true,
            inventoryHealth: true,
            inbound: true,
            moneyRecovery: true,
            doneForYou: true,
            summary: true,
          },
        });
      } else if (type === "current") {
        await generateInventoryPdf(data, {
          sellerName: (data.store && data.store.name) || "Seller",
          periodText: "This week",
          currency,
          sections: {
            actToday: true,
            dueThisWeek: currentTab === "actions",
            inventoryHealth: currentTab === "inventory",
            inbound: currentTab === "inbound",
            moneyRecovery: currentTab === "recovery",
            doneForYou: false,
            summary: true,
          },
        });
      }
    } catch (err) {
      console.error("Quick download failed:", err);
      alert("Failed to export PDF: " + (err.message || "Unknown error"));
    } finally {
      setQuickDownloading(false);
    }
  };

  const currentTabLabel = TAB_LABELS[currentTab] || "Current View";

  return (
    <div className="ia-snapshot">
      <div className="ia-snapshot__actions">
        <button
          type="button"
          className="ia-iconbtn"
          onClick={() => onReload()}
          disabled={loading}
          title="Reload the screens from stored data"
          aria-label="Reload"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className={loading ? "ia-spin" : ""}>
            <path
              fill="currentColor"
              d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            />
          </svg>
        </button>
        <button type="button" className="ia-btn ia-btn--primary" onClick={() => onFetch(force)} disabled={jobRunning}>
          {jobRunning ? "Fetching…" : "Fetch from Amazon"}
        </button>
        <button type="button" className="ia-btn" onClick={() => setShowDetail((v) => !v)} aria-expanded={showDetail}>
          Data times
        </button>

        {/* Seller Report (PDF) Button matching mockup */}
        <div className="ia-dropdown-wrap" ref={dropdownRef}>
          <button
            type="button"
            className="btn-icon btn-icon-left btn-background-color btn btn-secondary ia-btn--seller-report"
            onClick={() => setShowExportModal(true)}
            disabled={!data || loading || quickDownloading}
            title="Open Seller report (PDF)"
          >
            {quickDownloading ? (
              <svg viewBox="0 0 24 24" width="14" height="14" className="ia-spin" fill="currentColor" aria-hidden="true">
                <path d="M12 4V2C6.48 2 2 6.48 2 12h2c0-4.41 3.59-8 8-8z" />
              </svg>
            ) : null}
            <span>Seller report (PDF)</span>
            <span
              className="ia-dropdown-caret"
              onClick={(e) => {
                e.stopPropagation();
                setShowExportDropdown((v) => !v);
              }}
              title="Quick download options"
              role="button"
              tabIndex={0}
            >
              ▾
            </span>
          </button>

          {showExportDropdown && (
            <div className="ia-dropdown-menu">
              <button
                type="button"
                className="ia-dropdown-item"
                onClick={() => handleQuickDownload("full")}
              >
                <strong>Download Full Report (PDF)</strong>
                <small>All 6 sections & summary metrics</small>
              </button>
              <button
                type="button"
                className="ia-dropdown-item"
                onClick={() => handleQuickDownload("current")}
              >
                <strong>Download Current Tab (PDF)</strong>
                <small>{currentTabLabel}</small>
              </button>
              <div className="ia-dropdown-divider" />
              <button
                type="button"
                className="ia-dropdown-item"
                onClick={() => {
                  setShowExportDropdown(false);
                  setShowExportModal(true);
                }}
              >
                <strong>Customize Report Options…</strong>
                <small>Select sections, filters & urgency</small>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Download Modal */}
      {showExportModal && (
        <ReportDownloadModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          data={data}
          currentTab={currentTab}
          currency={currency}
        />
      )}

      <div className={`ia-snapshot__time${stale ? " ia-text--warn" : ""}`}>
        Data as of {dateTime(dataTime)}
        {dataTime ? ` (${ago(dataTime)})` : ""}
      </div>
      <label className="ia-snapshot__force">
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} disabled={jobRunning} />
        Request today's reports again
      </label>

      {job && job.job ? (
        <div className={`ia-job ia-job--${job.stage || "queued"}`} role="status">
          <strong>{STAGE_TEXT[job.stage] || job.message}</strong>
          {job.created === false && job.message ? <span>. {job.message}</span> : null}
          {reports ? (
            <span>
              . Reports ready: {reports.fetched + reports.noData} of {reports.requested}
              {reports.failed ? ` (${reports.failed} failed)` : ""}
            </span>
          ) : null}
          {typeof job.apiCallsSinceSubmit === "number" ? <span>. API calls: {job.apiCallsSinceSubmit}</span> : null}
          {jobRunning && reports && reports.requested ? (
            <div className="ia-progress">
              <i style={{ width: `${Math.round((100 * (reports.fetched + reports.noData)) / reports.requested)}%` }} />
            </div>
          ) : null}
        </div>
      ) : null}
      {jobError ? <div className="ia-job ia-job--failed">{jobError}</div> : null}

      {showDetail && snapshot ? (
        <div className="ia-card ia-snapshot__detail">
          <div>Screens built {dateTime(asOf)}</div>
          <div>Newest report {dateTime(snapshot.reportsFetchedAt)}</div>
          <div>Last live API call {dateTime(snapshot.lastApiCallAt)}</div>
          {snapshot.pendingToday ? <div>{snapshot.pendingToday} reports still being prepared by Amazon</div> : null}
          <table className="ia-table ia-table--compact">
            <thead>
              <tr>
                <th>Report</th>
                <th>Last fetched</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.reports.map((r) => (
                <tr key={r.key}>
                  <td>{r.reportType.replace(/^GET_|_DATA$/g, "").replace(/_/g, " ").toLowerCase()}</td>
                  <td className={isStale(r.lastFetchedAt, r.key === "storageFees" ? 24 * 35 : 50) ? "ia-text--warn" : ""}>{dateTime(r.lastFetchedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
